import axios, { type InternalAxiosRequestConfig } from 'axios';
import { getUserFacingApiError } from '../utils/apiErrorMessages';
import { hmacSha256Hex } from '../utils/hmacBodySignature';
import {
  getApplicationGuidFromStorage,
  getClientGuidFromStorage,
} from '../utils/sessionIds';


const LOCAL_DEV_QR_PUBLIC_HOST = "qr-mva.aspirion.com";

// const LOCAL_DEV_QR_PUBLIC_HOST = "qr-mva.aspirion.com";

function getXQrClientHostHeader(): string {
  if (typeof window === 'undefined' || !window.location?.host) return '';
  const { hostname, host } = window.location;

  if (/^(localhost|127\.0\.0\.1|\[::1\]|::1)$/i.test(hostname)) {
    return LOCAL_DEV_QR_PUBLIC_HOST;
  }

  return host;
}

const UUID_IN_PATH =
  /\/applications\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})(?:\/|$)/i;

function applicationGuidFromRequestUrl(url: string | undefined): string | null {
  if (!url || url.includes('reissue-draft-access')) return null;
  const m = url.match(UUID_IN_PATH);
  return m?.[1] ?? null;
}

/**
 * API base URL from `QR_APP_API_URL` (see `craco.config.js` + `.env`).
 * Production builds require it. Development: if unset, defaults to http://localhost:3001.
 */
function resolveApiBaseUrl(): string {
  const raw = process.env.QR_APP_API_URL?.trim();
  if (raw) {
    return raw.replace(/\/+$/, '');
  }
  if (process.env.NODE_ENV === 'production') {
    throw new Error(
      'QR_APP_API_URL must be set when building for production (e.g. https://api.example.com)'
    );
  }
  return 'http://localhost:3001';
}

const baseURL = resolveApiBaseUrl();

export const applicationApiClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  /** Required so `draft_access` HttpOnly cookie is sent on /api requests after create. */
  withCredentials: true,
});

let csrfToken: string | null = null;
/** In-flight fetch so parallel POST/PATCH (e.g. React Strict Mode double effects) share one token. */
let csrfInflight: Promise<string> | null = null;

export function clearCsrfToken(): void {
  csrfToken = null;
  csrfInflight = null;
}

async function ensureCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  if (!csrfInflight) {
    csrfInflight = applicationApiClient
      .get<{ csrfToken: string }>('/api/csrf-token')
      .then(({ data }) => {
        csrfToken = data.csrfToken;
        return csrfToken;
      })
      .finally(() => {
        csrfInflight = null;
      });
  }
  return csrfInflight;
}

applicationApiClient.interceptors.request.use(async (config) => {
  const method = config.method?.toLowerCase();
  if (method === 'post' || method === 'patch') {
    const token = await ensureCsrfToken();
    config.headers['X-CSRF-Token'] = token;
    let raw: string;
    if (typeof config.data === 'string') {
      raw = config.data;
    } else if (config.data === undefined || config.data === null) {
      raw = '';
    } else {
      raw = JSON.stringify(config.data);
    }
    config.data = raw;
    config.headers['X-Body-Signature'] = await hmacSha256Hex(token, raw);
  }
  return config;
});

applicationApiClient.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (!axios.isAxiosError(err) || !err.config) {
      return Promise.reject(err);
    }

    const data = err.response?.data as { error?: string } | undefined;
    const status = err.response?.status;
    const code = data?.error;

    if (status === 403 && code === 'invalid_csrf_token') {
      clearCsrfToken();
    }
    if (
      status === 401 &&
      (code === 'invalid_body_signature' || code === 'missing_body_signature')
    ) {
      clearCsrfToken();
    }

    const reissueable =
      status === 401 &&
      (code === 'invalid_or_expired_draft_token' || code === 'draft_access_required');

    type Cfg = InternalAxiosRequestConfig & { __sessionRestoreTried?: boolean };
    const cfg = err.config as Cfg;

    if (
      reissueable &&
      !cfg.__sessionRestoreTried &&
      typeof window !== 'undefined'
    ) {
      const url = cfg.url ?? '';
      const fromUrl = applicationGuidFromRequestUrl(url);
      const appGuid = fromUrl ?? getApplicationGuidFromStorage();
      const clientGuid = getClientGuidFromStorage();
      if (appGuid && clientGuid) {
        cfg.__sessionRestoreTried = true;
        try {
          await applicationApiClient.post(
            `/api/applications/${encodeURIComponent(appGuid)}/reissue-draft-access`,
            { clientGuid }
          );
          return applicationApiClient.request(cfg);
        } catch (reissueErr) {
          if (axios.isAxiosError(reissueErr) && reissueErr.response) {
            return Promise.reject(reissueErr);
          }
          return Promise.reject(err);
        }
      }
    }

    return Promise.reject(err);
  }
);

export interface WelcomeClientPublic {
  guid: string;
  fullName: string;
  phone: string | null;
}

/** Public client info for welcome page (no draft cookie required). */
export async function getWelcomeClient(guid: string): Promise<WelcomeClientPublic> {
  const headers: Record<string, string> = {};
  const clientHost = getXQrClientHostHeader();
  if (clientHost) {
    headers['X-QR-Client-Host'] = clientHost;
  }
  const { data } = await applicationApiClient.get<WelcomeClientPublic>(
    `/api/public/clients/${encodeURIComponent(guid)}`,
    { headers }
  );
  return data;
}

export interface ApplicationResponse {
  id: number;
  guid: string;
  clientGuid: string | null;
  formData: Record<string, unknown>;
  currentStep: string | null;
  isApplicantInvolved: boolean | null;
  incidentDate: string | null;
  additionalInfo: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateApplicationPayload {
  clientGuid: string;
  currentStep?: string;
}

/** User-safe message for toasts and alerts (never raw API or network internals). */
export function getErrorMessage(err: unknown): string {
  return getUserFacingApiError(err);
}

/** When set, UI should redirect to /oops instead of showing an alert. */
export function getOopsRedirectReason(
  err: unknown
): 'client_not_found' | 'client_disabled' | null {
  if (!axios.isAxiosError(err) || !err.response) return null;
  const code = (err.response.data as { error?: string } | undefined)?.error;
  if (code === 'client_not_found' || code === 'client_disabled') return code;
  if (code === 'client_mismatch' || code === 'service_line_required') return 'client_not_found';
  return null;
}

export async function createApplication(
  payload: CreateApplicationPayload
): Promise<ApplicationResponse> {
  const { data } = await applicationApiClient.post<ApplicationResponse>(
    '/api/applications',
    payload
  );
  return data;
}

export async function getApplication(guid: string): Promise<ApplicationResponse> {
  const { data } = await applicationApiClient.get<ApplicationResponse>(
    `/api/applications/${guid}`
  );
  return data;
}

export async function patchApplication(
  guid: string,
  body: Record<string, unknown>
): Promise<ApplicationResponse> {
  const { data } = await applicationApiClient.patch<ApplicationResponse>(
    `/api/applications/${guid}`,
    body
  );
  return data;
}
